import React, { createContext, useContext, ReactNode, useState } from "react"
import { useFormik, FormikProps } from "formik"
import * as Yup from "yup"
import { useAuth } from "../../../../AppContext"

// Définition de la structure des données du formulaire
interface KpiFormValues {
     responsables: number[]
     dateDebut: string
     dateFin: string
     indicators: string[]
     _timestamp?: number // Marqueur pour forcer la mise à jour si les filtres sont identiques
}

// Interface du contexte incluant Formik et nos états personnalisés
interface KpiContextType extends FormikProps<KpiFormValues> {
     isSubmitted: boolean
     setIsSubmitted: (val: boolean) => void
     showForm: boolean
     setShowForm: (val: boolean) => void
     appliedValues: KpiFormValues | null // Valeurs envoyées à l'API
     error: string | null // Stockage de l'erreur backend
     setError: (val: string | null) => void
}

const KpiContext = createContext<KpiContextType | undefined>(undefined)

export const KpiProvider = ({ children }: { children: ReactNode }) => {
     // États de contrôle du workflow
     const [isSubmitted, setIsSubmitted] = useState(false)
     const [showForm, setShowForm] = useState(true)
     const [appliedValues, setAppliedValues] = useState<KpiFormValues | null>(null)
     const [error, setError] = useState<string | null>(null)
     const {currentUser} = useAuth()

     // Schéma de validation Yup
     const currentYear = new Date().getFullYear()
     const firstDayOfYear = `${currentYear}-01-01`
     const lastDayOfYear = `${currentYear}-12-31`

     const validationSchema = Yup.object().shape({
          responsables: Yup.array().min(1, "Au moins un responsable requis"),
          dateDebut: Yup.string().required("Date de début requise"),
          dateFin: Yup.string().required("Date de fin requise"),
          indicators: Yup.array().min(1, "Au moins un indicateur requis"),
     })

     // Configuration de Formik
     const formik = useFormik<KpiFormValues>({
          initialValues: {
               responsables: [...(currentUser?.personnelHierarchyFromUpToDown?.map(itm => itm.id!) || []), currentUser?.personnel?.id!],
               dateDebut: firstDayOfYear,
               dateFin: lastDayOfYear,
               indicators: ["RENTABILITE", "EFFECTIFS", "INTERCONTRATS"],
          },
          validationSchema,
          onSubmit: async (values, { setSubmitting }) => {
               /**
                * NOTE: On ne fait plus setError(null) ici.
                * C'est KpiCharts qui le fera au début de fetchKpi() pour éviter
                * que le container d'erreur ne clignote ou disparaisse trop tôt.
                */

               // On injecte un timestamp pour garantir que l'objet change à chaque clic
               setAppliedValues({ ...values, _timestamp: Date.now() })

               setIsSubmitted(true)
               setShowForm(false)

               // On libère le bouton pour permettre de nouvelles tentatives
               setSubmitting(false)
          },
     })

     return (
          <KpiContext.Provider
               value={{
                    ...formik,
                    isSubmitted,
                    setIsSubmitted,
                    showForm,
                    setShowForm,
                    appliedValues,
                    error,
                    setError,
               }}
          >
               {children}
          </KpiContext.Provider>
     )
}

/**
 * Hook pour accéder facilement au contexte dans les composants enfants
 */
export const useKpiForm = () => {
     const context = useContext(KpiContext)
     if (!context) {
          throw new Error("useKpiForm must be used within a KpiProvider")
     }
     return context
}
